/**
 * @file useSiteViewState.ts
 * @description Состояние каркаса «вида сайта» (phase19.16 Р1): загрузка
 * черновика и «что изменено», адрес сайта, права по частям, открытое окно.
 * Значение кладёт в контекст `SiteViewLayout`.
 *
 * «Что изменено» перечитывается после каждой сохранённой правки с задержкой:
 * серия правок цвета даёт один запрос, а не десять (тот же отпечаток
 * черновика нужен окну публикации — SB51).
 */

import { editableSiteViewParts } from "@chaiberi/shared";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useSiteDraftParts } from "./useSiteDraftParts";
import { useSitePreviewState } from "./useSitePreviewState";
import { useAuth } from "../../auth/hooks";
import type { PublishPageTarget, SiteViewContextValue, SiteViewDialog } from "../../components/site-view/site-view-context";
import { siteView } from "../../lib/api-site-view";
import { sitePages } from "../../lib/api-site-pages";
import { siteViewErrorText } from "../../lib/site-view/errors";
import type { SiteViewChanges, SiteViewDraft } from "../../lib/site-view/types";

/** Задержка перечитывания «что изменено» после правки, мс. */
const CHANGES_DELAY_MS = 600;

export function useSiteViewState(enabled: boolean): SiteViewContextValue {
  const auth = useAuth();
  const permissions = auth.me?.activeContext?.role.permissions;
  const [draft, setDraft] = useState<SiteViewDraft | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [changes, setChanges] = useState<SiteViewChanges | null>(null);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [dialog, setDialog] = useState<SiteViewDialog>(null);
  const [pageTarget, setPageTarget] = useState<PublishPageTarget | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadChanges = useCallback(async () => {
    try {
      setChanges(await siteView.changes());
    } catch {
      // Полоса черновика не критична: экран работает и без неё.
    }
  }, []);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDraft(await siteView.draft());
      void loadChanges();
    } catch (err) {
      setError(siteViewErrorText(err, "Не удалось загрузить вид сайта. Сайт работает в опубликованном виде — ничего не потеряно."));
    } finally {
      setLoading(false);
    }
  }, [loadChanges]);

  useEffect(() => {
    if (!enabled) return;
    void reload();
    // Адрес сайта — из настроек предпросмотра (домены магазинов — phase19.28).
    void sitePages
      .previewConfig()
      .then((cfg) => setSiteUrl(cfg.webBaseUrl))
      .catch(() => setSiteUrl(null));
  }, [enabled, reload]);

  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const refreshChanges = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void loadChanges(), CHANGES_DELAY_MS);
  }, [loadChanges]);

  const applyPart = useCallback<SiteViewContextValue["applyPart"]>(
    (part, patch) => {
      setDraft((prev) => (prev ? { ...prev, ...patch, parts: { ...prev.parts, [part.part]: part } } : prev));
      refreshChanges();
    },
    [refreshChanges],
  );

  const replaceDraft = useCallback(
    (next: SiteViewDraft) => {
      setDraft(next);
      void loadChanges();
    },
    [loadChanges],
  );

  const parts = useSiteDraftParts(draft, applyPart);
  const preview = useSitePreviewState();
  const editableParts = useMemo(() => editableSiteViewParts(permissions ?? []), [permissions]);

  return {
    draft,
    parts,
    preview,
    loading,
    error,
    reload,
    changes,
    refreshChanges,
    applyPart,
    replaceDraft,
    editableParts,
    currentUserId: auth.me?.user.id ?? null,
    siteUrl,
    dialog,
    openDialog: setDialog,
    pageTarget,
    setPageTarget,
  };
}
